import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, IconButton, Skeleton, Stack, Typography, Button } from '@mui/material';
import { Policy } from 'views/App/Portal/Policy/_types/policy';
import parse, { DOMNode } from 'html-react-parser';
import { Download, Description } from '@mui/icons-material';
import { warning } from '_lex/_foundations/phxColors';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import EditorSheet from '_lex/_atoms/EditorSheet';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';

function WrittenDirectivePolicy({
  manualId,
  manual,
  policy,
  scrollId,
}: {
  manualId: string;
  manual?: Manual;
  policy?: Policy;
  scrollId?: string;
}) {
  const [params] = useSearchParams();
  const [showFullPolicy, setShowFullPolicy] = useState(false);

  useEffect(() => {
    if (scrollId && policy) {
      const element = document.getElementById(`polsec_${scrollId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [scrollId, policy]);

  const filteredSections = policy?.sections.filter(
    (section) =>
      showFullPolicy ||
      section.id.toString() === scrollId ||
      section.subSections.some((sub) => sub.id.toString() === scrollId)
  );

  return (
    <Box className='written-directive-policy'>
      {!showFullPolicy && policy && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='text'
            size='small'
            startIcon={<Description />}
            onClick={() => setShowFullPolicy(true)}
            sx={{ mb: 2 }}
          >
            View Full Policy
          </Button>
        </Box>
      )}
      <EditorSheet>
        <Stack spacing={10}>
          {!policy ? (
            <Stack spacing={2}>
              <Skeleton
                variant='rectangular'
                height={28}
                width='50%'
              />
              <Stack>
                <Skeleton variant='text' />
                <Skeleton variant='text' />
                <Skeleton variant='text' />
              </Stack>
            </Stack>
          ) : (
            <>
              <Stack spacing={3}>
                <Typography variant='subtitleMd'>{manual?.name || ''}</Typography>
                <Typography variant='subtitleMd'>Policy {policy?.key || ''}</Typography>
                <Typography variant='displaySm'>{policy?.title || ''}</Typography>
              </Stack>

              {filteredSections?.map((section) => (
                <Fragment key={`section_${section.id}`}>
                  <Stack
                    id={`polsec_${section.id}`}
                    sx={{
                      backgroundColor:
                        section.id.toString() === scrollId
                          ? warning[100]
                          : section.id.toString() === params.get('s')
                            ? warning[50]
                            : 'transparent',
                    }}
                  >
                    <Typography variant='h5'>{[section?.key, section?.title].join(': ')}</Typography>
                    {parse(section.content || '', {
                      replace(node: DOMNode, ind: number) {
                        return PolicyContentHelper.handleContent(node, ind);
                      },
                    })}
                  </Stack>

                  {section.subSections
                    .filter((subSection) => showFullPolicy || subSection.id.toString() === scrollId)
                    .map((subSection) => (
                      <Stack
                        id={`polsec_${subSection.id}`}
                        key={`subsection_${section.id}_${subSection.id}`}
                        sx={{
                          backgroundColor:
                            subSection.id.toString() === scrollId
                              ? warning[100]
                              : subSection.id.toString() === params.get('s')
                                ? warning[50]
                                : 'transparent',
                        }}
                      >
                        <Typography variant='h6'>{[subSection.key, subSection.title].join(': ')}</Typography>
                        {parse(subSection.content || '', {
                          replace(node: DOMNode, ind: number) {
                            return PolicyContentHelper.handleContent(node, ind);
                          },
                        })}
                      </Stack>
                    ))}
                </Fragment>
              ))}
            </>
          )}
        </Stack>
      </EditorSheet>
    </Box>
  );
}

export default WrittenDirectivePolicy;
